import React, {
  useEffect,
  useContext,
  useState,
} from 'react';
import { useHistory, useParams } from 'react-router';
import { FormattedMessage, useIntl } from 'react-intl';

import getConfig from '../../config';
import {
  getUpdatedDate, handleResponseStatus, sendRequest,
} from '../../utils/api-request';
import {
  checkExistingDataUpload,
  getCurrentProjectStage,
  getProjectReference,
} from '../../utils/helpers';
import {
  ACTIVE_STATUS,
  REGISTER_STATUS,
  PAUSED_STATUS,
  FAILED_STATUS,
  CLOSED_STATUS,
  TERMINATED_STATUS,
} from '../../constans/constans';
import { GAS } from '../../state/near';
import {
  getContract, contractMethods, getTxOutcome, isWalletCancelError,
} from '../../utils/near-utils';
import { appStore } from '../../state/app';
import { initIPFS } from '../../state/ipfs';

import SubzoneModal from '../ProjectPage/components/SubzoneModal/SubzoneModal';
import StepWizard from '../../components/WizardForm/Steps/StepWizard';
import Loader from '../../components/Loader/Loader';
import RequestModal from '../../components/RequestModal/RequestModal';
import SampleData from './components/SampleData';
import DocumentData from './components/DocumentData';
import PreviewReport from './components/PreviewReport';
import { queueToastifyMessage } from '../../components/Toastify/Toastify';

const { indexerApi, deposit } = getConfig();

const StageReport = () => {
  const history = useHistory();
  const { state } = useContext(appStore);
  const { account } = state;
  const translate = useIntl();

  const currentDate = Date.now() * 1e6;

  const { nameId } = useParams();

  const [totalData, setTotalData] = useState({});
  const [projectStatus, setProjectStatus] = useState('');
  const [previewData, setPreviewData] = useState({});
  const [step, setStep] = useState(0);
  const [loadingData, setLoading] = useState(true);
  const [currentStage, setCurrentStage] = useState();
  const [lastStageEndTime, setLastStageEndTime] = useState(0);
  const [lastStageStartTime, setLastStageStartTime] = useState(0);
  const [stageDuration, setStageDuration] = useState('');
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [existingReportModal, setExistingReportModal] = useState(false);
  const [reloadError, setReloadError] = useState(false);
  const stepForm = [
    { id: 'uploadWizard.monitoringData' },
    { id: 'uploadWizard.additionalData' },
    { id: 'uploadWizard.preview' },
  ];

  const nextPage = () => {
    setStep(step + 1);
  };

  const prevPage = () => {
    setStep(step - 1);
  };

  const failFunc = () => {
    setLoading(false);
    setShowRequestModal(true);
  };

  const confirmDataUpload = async (cid, stageId) => {
    const contract = getContract(account, contractMethods, 0);
    const prevReference = await getProjectReference(contract, totalData.project_id);

    if (cid && prevReference) {
      const txResult = await contract.data_upload({
        project_id: nameId,
        stage_id: stageId.toString(),
        reference: `/ipfs/${cid}`,
        prev_reference: prevReference,
      }, GAS, deposit);
      const { isSuccess, methodName } = getTxOutcome(txResult);
      if (!isSuccess || methodName !== 'data_upload') {
        if (isWalletCancelError(txResult)) {
          return;
        }
        throw new Error('data_upload tx was not successful');
      }
      queueToastifyMessage(translate.formatMessage({ id: 'smart-contract-notify' }));
      history.push('/');
    } else failFunc();
  };

  const handleMintProject = async () => {
    try {
      const contract = getContract(account, contractMethods, 0);
      const txResult = await contract.update_project_status({
        project_id: totalData.project_id,
        project_status: ACTIVE_STATUS,
      }, GAS, deposit);
      const { isSuccess, methodName } = getTxOutcome(txResult);
      if (!isSuccess || methodName !== 'update_project_status') {
        if (isWalletCancelError(txResult)) {
          return;
        }
        throw new Error('update_project_status tx was not successful');
      }
      queueToastifyMessage(translate.formatMessage({ id: 'smart-contract-notify' }));
      history.push('/');
    } catch (e) {
      if (isWalletCancelError(e)) {
        return;
      }
      failFunc();
    }
  };

  const handleDataUpload = async (dataProject) => {
    try {
      const ipfs = await initIPFS();
      const { path } = await ipfs.add(JSON.stringify(dataProject));
      const compareData = await getUpdatedDate(dataProject.project_id);

      if (dataProject?.updatedAt !== compareData?.updatedAt) {
        return setReloadError(true);
      }
      if (typeof path === 'string') await confirmDataUpload(path, currentStage);
      else failFunc();
    } catch (e) {
      if (isWalletCancelError(e)) {
        return;
      }
      failFunc();
    }
  };

  const getStageDuration = () => {
    const divider = 1e6;
    const stageStart = new Date(lastStageStartTime / divider);
    const stageEnd = new Date(lastStageEndTime / divider);

    const formattedStart = `${stageStart.toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' })}`;
    const formattedEnd = `${stageEnd.toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' })}`;
    return `${formattedStart} - ${formattedEnd}`;
  };

  useEffect(async () => {
    const successFunc = async (data) => {
      const token = data?.hits?.hits?.length && data?.hits?.hits[0]._source;
      if (token) {
        const currentStageId = getCurrentProjectStage(token.stages);
        const numberOfStage = currentStageId < 0 ? (token.stages.length - 1) : currentStageId;
        if (token.stages?.length) setLastStageEndTime(token.stages[numberOfStage].ends_at);
        setLastStageStartTime(token.stages[numberOfStage].starts_at);
        const isDataUploadExist = checkExistingDataUpload(token.stages, currentStageId);

        // If data upload (stage report) was already done
        if (isDataUploadExist) {
          setLoading(false);
          setExistingReportModal(true);
          return;
        }

        setTotalData(token);
        setProjectStatus(token.status?.toLowerCase());
        setCurrentStage(currentStageId);
        setLoading(false);
      } else failFunc();
    };

    if (account) {
      const bodyReq = {
        query: {
          bool: {
            must: [
              {
                match: {
                  'project_id.keyword': nameId,
                },
              },
              {
                match: {
                  'signer_id.keyword': account.accountId,
                },
              },
            ],
          },
        },
      };

      const { result, statusCode } = await sendRequest(indexerApi, 'POST', JSON.stringify(bodyReq), false);
      handleResponseStatus(
        statusCode,
        () => successFunc(result),
        () => failFunc(),
      );
    }
  }, [account]);

  useEffect(() => {
    setStageDuration(getStageDuration());
  }, [lastStageStartTime]);

  if (loadingData) {
    return (
      <div className="dashboard__loader">
        <Loader />
      </div>
    );
  }

  // If first render request failed
  if (!Object.keys(totalData).length && showRequestModal) {
    return (
      <RequestModal
        error
        handleClose={() => history.push('/')}
        title={translate.formatMessage({ id: 'modal.title.oops' })}
        description={(
          <FormattedMessage
            id="modal.description.goesWrong"
            values={{
              link: (...chunks) => <a href="mailto:support@openforestprotocol.org" className="text-anchor">{chunks}</a>,
            }}
          />
)}
      />
    );
  }

  // If last stage is finished
  if (lastStageEndTime > 0 && lastStageEndTime < currentDate) {
    return (
      <RequestModal
        handleClose={() => history.push('/')}
        title={translate.formatMessage({ id: 'modal.title.congratulations' })}
        description={translate.formatMessage({ id: 'modal.description.finished' })}
      />
    );
  }

  // If stage report was done
  if (existingReportModal) {
    return (
      <div className="upload-wizard-flexCenter">
        <div className="wizard__popup-logo">
          <i className="icon-check-circle" />
        </div>
        <div className="wizard__popup-desc">
          <h2 className="title">{translate.formatMessage({ id: 'modal.title.congratulations' })}</h2>
          <div className="text-block">
            <p className="text paragraphIndents">{translate.formatMessage({ id: 'modal.title.sentToValidators' })}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!projectStatus) {
    return (
      <RequestModal
        error
        handleClose={() => history.push('/')}
        title={translate.formatMessage({ id: 'modal.title.wentWrong' })}
        description={translate.formatMessage({ id: 'modal.description.canNotTrack' })}
        buttonText={translate.formatMessage({ id: 'modal.button.return' })}
      />
    );
  }
  if (reloadError) {
    return (
      <RequestModal
        error
        handleClose={() => window.location.reload()}
        title={translate.formatMessage({ id: 'modal.title.reload' })}
        description={translate.formatMessage({ id: 'modal.description.reload' })}
        buttonText={translate.formatMessage({ id: 'modal.button.reload' })}
      />
    );
  }

  return (
    <div className="data-upload">

      {showRequestModal && (
        <RequestModal
          error
          handleClose={() => setShowRequestModal(false)}
          title={translate.formatMessage({ id: 'modal.title.oops' })}
          description={(
            <FormattedMessage
              id="modal.description.somethingGoesWrong"
              values={{
                link: (...chunks) => <a href="mailto:support@openforestprotocol.org" className="text-anchor">{chunks}</a>,
              }}
            />
)}
        />
      )}
      {projectStatus === REGISTER_STATUS && (
        <SubzoneModal
          closeHandler={() => history.push('/')}
          submitHandler={() => handleMintProject()}
          closeBtnText={translate.formatMessage({ id: 'button-back' })}
          submitBtnText={translate.formatMessage({ id: 'button-confirm' })}
          modalText={(
            <FormattedMessage
              id="stageReport.modal.description.registerStatus"
              values={{
                span: (...chunks) => <span>{chunks}</span>,
                bold: (chunks) => <b>{chunks}</b>,
                br: <br />,
              }}
            />
          )}
        />
      )}
      {projectStatus === PAUSED_STATUS && (
        <SubzoneModal
          closeHandler={() => history.push('/')}
          submitHandler={() => history.push('/')}
          closeBtnText={translate.formatMessage({ id: 'button-back' })}
          submitBtnText={translate.formatMessage({ id: 'button-gotIt' })}
          modalText={translate.formatMessage({ id: 'stageReport.modal.description.pausedStatus' })}
        />
      )}
      {projectStatus === FAILED_STATUS && (
        <SubzoneModal
          closeHandler={() => history.push('/')}
          submitHandler={() => history.push('/')}
          closeBtnText={translate.formatMessage({ id: 'button-back' })}
          submitBtnText={translate.formatMessage({ id: 'button-gotIt' })}
          modalText={translate.formatMessage({ id: 'stageReport.modal.description.failedStatus' })}
        />
      )}
      {projectStatus === TERMINATED_STATUS && (
        <SubzoneModal
          closeHandler={() => history.push('/')}
          submitHandler={() => history.push('/')}
          closeBtnText={translate.formatMessage({ id: 'button-back' })}
          submitBtnText={translate.formatMessage({ id: 'button-gotIt' })}
          modalText={translate.formatMessage({ id: 'stageReport.modal.description.failedStatus' })}
        />
      )}
      {projectStatus === CLOSED_STATUS && (
        <SubzoneModal
          closeHandler={() => history.push('/')}
          submitHandler={() => history.push('/')}
          closeBtnText={translate.formatMessage({ id: 'button-back' })}
          submitBtnText={translate.formatMessage({ id: 'button-gotIt' })}
          modalText={translate.formatMessage({ id: 'stageReport.modal.description.endedStatus' })}
        />
      )}
      {projectStatus === ACTIVE_STATUS && (
        <>
          <section className="upload-wizard">
            <div className="upload-wizard__wrapper">
              <div className="upload-wizard__col-header">
                <h2 className="upload-wizard__title">

                  {translate.formatMessage({ id: 'stageReport.modal.mintedStatus.uploadStageReport' })}
                </h2>
                <div className="previous-reports__stage-calendar">
                  <span className="previous-reports__stage-calendar-bold">
                    {translate.formatMessage({ id: 'stageTimeline.stage' })}
                    {' '}
                    {currentStage + 1}
                    {' '}
                    -
                  </span>
                  <i className="icon-event" />
                  <span>
                    {stageDuration}
                  </span>
                </div>
              </div>
              <div className="upload-wizard__body">
                {state.loading && (
                  <div className="cropper__loader">
                    <Loader />
                  </div>
                )}
                {totalData?.stagesInfo && (
                  <>
                    <StepWizard
                      step={step}
                      stepForm={stepForm}
                      showIcon={false}
                    />
                    <SampleData
                      step={step}
                      totalData={totalData}
                      setTotalData={setTotalData}
                      nextPage={nextPage}
                      prevPage={() => history.push('/')}
                      currentStage={currentStage}
                      setPreviewData={setPreviewData}
                      setShowRequestModal={setShowRequestModal}
                    />
                    <DocumentData
                      step={step}
                      totalData={totalData}
                      setTotalData={setTotalData}
                      nextPage={nextPage}
                      prevPage={prevPage}
                      currentStage={currentStage}
                      setShowRequestModal={setShowRequestModal}
                    />
                    <PreviewReport
                      step={step}
                      totalData={totalData}
                      previewData={previewData}
                      nextPage={nextPage}
                      prevPage={prevPage}
                      currentStage={currentStage}
                      handleDataUpload={handleDataUpload}
                    />
                  </>
                )}
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default React.memo(StageReport);
